import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { firstValueFrom, map, startWith } from 'rxjs';

import { buildV3Url, isNonAngularUrl } from '../../helper/url.helper';
import { CoBrandingService, LoadingService } from '../../services';
import { UserService } from '../../user';
import { BrandLogoComponent } from '../brand-logo/brand-logo.component';
import { ButtonComponent, InputComponent, SpinnerComponent } from '../form';
import { ResetPasswordComponent } from '../modal';
import { ModalService } from '../modal/modal.service';
import { LoginErrorsComponent } from './login-errors/login-errors.component';
import { QrCodeComponent } from './qr-code/qr-code.component';

export type Login401ResponseStatus = {
  needsTotpCode?: boolean;
  error?: string;
  totpQRCode?: string;
  secret?: string;
  id?: number;
};

const validateTotp: ValidatorFn = (control) => {
  const v = control.value;
  if (typeof v !== 'string') {
    return null;
  }

  if (/^\d{6}$/.test(v)) {
    return null;
  }

  return {
    totp: $localize`:@@login.error.totp-invalid:Must be 6 digits`,
  };
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dmb-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    AsyncPipe,
    LoginErrorsComponent,
    QrCodeComponent,
    ButtonComponent,
    SpinnerComponent,
    BrandLogoComponent,
  ],
})
export class LoginComponent {
  private coBrandService = inject(CoBrandingService);
  private userService = inject(UserService);
  private fb = inject(FormBuilder);
  modalService = inject(ModalService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private urlSerializer = inject(UrlSerializer);
  loading = inject(LoadingService);
  isWhiteLabelBrand = this.coBrandService.isCoBrand;

  readonly username = viewChild('username', { read: ElementRef });
  readonly password = viewChild('password', { read: ElementRef });
  errorStatus = signal(0);
  errorMessage = signal('');
  image = signal<string | undefined>(undefined);
  isLoggedIn = this.userService.authenticatedUser.pipe(
    map(() => true),
    startWith(false),
  );
  realName = this.userService.user.pipe(map((user) => user.fullName));
  showTotpField = signal(false);

  user = this.fb.nonNullable.control('', [Validators.required]);
  pass = this.fb.nonNullable.control('', [Validators.required]);
  totp = this.fb.nonNullable.control('', [validateTotp]);

  webAuthFormGroup = this.fb.nonNullable.group({
    user: this.user,
    pass: this.pass,
    totp: this.totp,
    browserFingerprint: this.fb.nonNullable.control(''),
    withException: this.fb.nonNullable.control(false),
    respectTotpRequirement: this.fb.nonNullable.control(true),
  });

  passwordLost() {
    this.modalService.openDialog(ResetPasswordComponent);
  }

  async onSubmit($event: Event): Promise<void> {
    $event.preventDefault();
    const login = this.webAuthFormGroup.value;
    if (!(login.user && login.pass)) return;

    try {
      this.loading.setLoading(true);
      const res = await firstValueFrom(
        this.userService.authenticateWithFingerPrint(this.webAuthFormGroup.getRawValue()),
      );
      await firstValueFrom(this.userService.authenticatedUser);
      this.loading.setLoading(false);
      const redirUrl = res.redirectTo || this.route.snapshot.queryParams['comefrom'];
      if (redirUrl) await this.router.navigate([isNonAngularUrl(redirUrl) ? buildV3Url(redirUrl) : redirUrl]);
    } catch (err) {
      this.loading.setLoading(false);
      if (err instanceof HttpErrorResponse) await this.handleLoginError(err);
      else throw err;
    }
  }

  // this should be handled by the service.
  private async handleLoginError(err: HttpErrorResponse) {
    if (!err.message) this.errorStatus.set(1);

    if (err.status === 401) {
      const errorStatusMessage: Login401ResponseStatus = err.error;

      this.errorMessage.set(errorStatusMessage.error || '');
      this.showTotpField.set(!!errorStatusMessage.needsTotpCode);

      if (errorStatusMessage.totpQRCode) {
        this.image.set(errorStatusMessage.totpQRCode);
      }
      this.errorStatus.set(errorStatusMessage.error ? err.status : 0);
      if (errorStatusMessage.error === 'error.login.password-expired') {
        const url = '/index.php';
        const urlTree = this.urlSerializer.parse(url);

        const { error, ...qp } = errorStatusMessage;
        urlTree.queryParams = { ...qp, page: 'password-reset' };

        /**
         * @todo this is a short term hack to make redirection work locally as well as when deployed.
         * when the password reset dialog is moved to angular this should disappear.
         */
        return setTimeout(
          () =>
            (window.location.href = `${window.location.protocol}//${window.location.hostname}${urlTree.toString()}`),
          2000,
        );
      }
      return false;
    }
    this.errorStatus.set(err.status);
    return true;
  }
}
